import { Navigate, Outlet, NavLink, Link } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from 'react-query'
import { useTranslation } from 'react-i18next'
import { getCurrentUser, logout } from '../services/auth'
import { getSelf, updateProfile } from '../services/users'
import { onMutateSuccess } from '../utils/queryResponse'
import LanguageSwitcher from '../components/languageSwitcher'
import Avatar from '../components/avatar'
import './platformLayout.css'

const ICONS = {
    dashboard: <><rect x="3" y="3" width="7" height="7" /><rect x="14" y="3" width="7" height="7" /><rect x="3" y="14" width="7" height="7" /><rect x="14" y="14" width="7" height="7" /></>,
    companies: <><rect x="4" y="2" width="16" height="20" rx="2" /><path d="M9 22v-4h6v4" /><path d="M8 6h.01" /><path d="M16 6h.01" /><path d="M8 10h.01" /><path d="M16 10h.01" /><path d="M8 14h.01" /><path d="M16 14h.01" /></>,
    users: <><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" /><path d="M23 21v-2a4 4 0 0 0-3-3.87" /><path d="M16 3.13a4 4 0 0 1 0 7.75" /></>,
    plans: <><path d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z" /><path d="M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z" /></>,
    profile: <><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" /><circle cx="12" cy="7" r="4" /></>,
}

// One list drives both the desktop sidebar and the mobile bottom tab bar.
const NAV = [
    { to: '/platform', end: true, icon: 'dashboard', label: 'platform.layout.dashboard' },
    { to: '/platform/companies', icon: 'companies', label: 'platform.layout.companies' },
    { to: '/platform/users', icon: 'users', label: 'platform.layout.users' },
    { to: '/platform/plans', icon: 'plans', label: 'platform.layout.plans' },
]

const NavIcon = ({ children, className = 'pl-nav-ic', size = 18 }) => (
    <svg className={className} viewBox="0 0 24 24" width={size} height={size} fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">{children}</svg>
)

const PlatformSidebar = () => {
    const { t } = useTranslation()

    return (
        <nav className="pl-sidebar">
            <div className="pl-sidebar-inner">
                <div className="pl-sidebar-top">
                    <Link to="/platform" className="pl-brand">
                        <img src={process.env.PUBLIC_URL + '/logo-soroby.svg'} alt="Soroby" className="pl-brand-logo" />
                    </Link>
                </div>

                <div className="pl-nav-section-label">{t('platform.layout.overview')}</div>
                <ul className="pl-nav">
                    {NAV.map(item => (
                        <NavLink key={item.to} to={item.to} end={item.end}>
                            <li className="pl-nav-item">
                                <NavIcon>{ICONS[item.icon]}</NavIcon>
                                {t(item.label)}
                            </li>
                        </NavLink>
                    ))}
                </ul>
            </div>
        </nav>
    )
}

/**
 * Mobile only (hidden ≥768px via CSS): the main app's bottom tab bar
 * (.bottom-nav / .bn-tab) with the panel's pages plus Profile, which on
 * desktop sits behind the user chip in the top bar.
 */
const PlatformBottomNav = () => {
    const { t } = useTranslation()
    const tabs = [...NAV, { to: '/platform/profile', icon: 'profile', label: 'profile.profile' }]

    return (
        <nav className="bottom-nav d-md-none" role="navigation" aria-label={t('nav.primaryNavigation')}>
            {tabs.map(item => (
                <NavLink key={item.to} to={item.to} end={item.end} className="bn-tab">
                    <NavIcon className="bn-ic" size={22}>{ICONS[item.icon]}</NavIcon>
                    <span className="bn-label">{t(item.label)}</span>
                </NavLink>
            ))}
        </nav>
    )
}

const PlatformTopBar = ({ currentUser }) => {
    const { t } = useTranslation()
    const queryClient = useQueryClient()

    // Same ['profile'] query as the main navbar and the profile page, so a new
    // name or photo saved on /platform/profile shows here straight away.
    const { data: userData } = useQuery(['profile'], getSelf)
    const user = userData?.code === 200 ? userData.message : currentUser

    // Like the main navbar: remember the language on the profile too, so it
    // follows the user to other browsers and the profile page agrees with it.
    const { mutate: mutateLocale } = useMutation(updateProfile, {
        onSuccess: onMutateSuccess((res) => {
            if (res?.token) localStorage.setItem('cleaning-crm-token', res.token)
        })
    })

    const saveLanguageToProfile = (code) => {
        queryClient.setQueryData(['profile'], (old) => old ? { ...old, message: { ...old.message, locale: code } } : old)
        mutateLocale({ locale: code })
    }

    const handleLogout = () => {
        logout()
        window.location.assign('/login')
    }

    return (
        <header className="pl-topbar">
            <div className="pl-topbar-left">
                {/* Mobile: the logo takes the title's place, as in the main app. */}
                <Link to="/platform" className="pl-mlogo" aria-label="Soroby">
                    <img src={process.env.PUBLIC_URL + '/logo-soroby.svg'} alt="Soroby" />
                </Link>
                <span className="pl-topbar-title">{t('platform.layout.panelTitle')}</span>
            </div>
            <div className="pl-topbar-right">
                <LanguageSwitcher className="no-caret" id="platform-language-drop" align="end" onChange={saveLanguageToProfile} />
                {/* Same user chip + power button as the main app's top bar. */}
                <Link to="/platform/profile" className="sb-userchip d-none d-md-inline-flex" title={t('nav.yourProfile')}>
                    <Avatar className="sb-uava" size={30} name={user?.fullName || ''} src={user?.avatarUrl} />
                    <span className="sb-uname text-capitalize">{user?.fullName}</span>
                </Link>
                <button type="button" className="sb-iconbtn sb-iconbtn--danger" onClick={handleLogout} title={t('platform.layout.signOut')} aria-label={t('platform.layout.signOut')}>
                    <i className="fa-solid fa-power-off"></i>
                </button>
            </div>
        </header>
    )
}

const PlatformLayout = () => {
    const currentUser = getCurrentUser()

    if (!currentUser) return <Navigate to="/login" />
    if (!currentUser.isPlatformOwner) return <Navigate to="/" />

    return (
        <div className="pl-shell">
            <PlatformSidebar />
            <div className="pl-main">
                <PlatformTopBar currentUser={currentUser} />
                <div className="pl-content">
                    <Outlet />
                </div>
            </div>
            <PlatformBottomNav />
        </div>
    )
}

export default PlatformLayout
